/**
 * useWizardStore.ts
 *
 * Zustand store for the two-bar interactive presentation wizard (Sprint 1).
 *
 * Architecture:
 *  - Bar 1 (Setup): steps 1–5 — City, Plantilla, Tema, Proveedores, Detalles.
 *  - Bar 2 (Build): steps 1–6 — Preguntas, Planificación, Diapositivas, Imágenes, Costos, Generar.
 *
 * State and Actions interfaces are split per project convention.
 */

import { create } from 'zustand';

import type {
  WizardBar,
  SetupStep,
  BuildStep,
  NarrativePlan,
  SlideConfig,
  ImageSlotState,
  ReferenceImage,
  CostTableConfig,
  WizardQuestion,
} from '@/types/wizardV2';
import type { WizardFormData, ProviderSummary } from '@/types/wizard';

// ─── Default form data ───────────────────────────────────────────────────────

const INITIAL_FORM_DATA: WizardFormData = {
  city: '',
  plantilla_slug: '',
  theme_slugs: [],
  event_name: '',
  client_name: '',
  event_date: '',
  venue: '',
  attendees: '',
  prompt: '',
  provider_ids: [],
  logo_url: '',
};

// ─── State interface ─────────────────────────────────────────────────────────

interface WizardState {
  /**
   * UUID of the presentation record created on the backend once the user
   * selects a city. `null` before the draft is created.
   */
  presentationId: string | null;

  // ── Two-bar navigation ──────────────────────────────────────────────────

  /** Which wizard bar is currently visible. */
  activeBar: WizardBar;
  /** Currently active step within the Setup bar. */
  setupStep: SetupStep;
  /** Currently active step within the Build bar. */
  buildStep: BuildStep;
  /**
   * The highest Setup step the user has reached (used to enable/disable
   * forward-navigation in the step indicator).
   */
  highestSetupStep: number;
  /**
   * The highest Build step the user has reached.
   */
  highestBuildStep: number;
  /**
   * Flag set to `true` while the bar-transition animation is playing.
   * Prevents navigation interactions during the animation window.
   */
  isTransitioning: boolean;

  // ── Setup data ──────────────────────────────────────────────────────────

  /** Shared form data collected across the five Setup steps. */
  formData: WizardFormData;
  /** Full provider objects selected in step 4 (Proveedores). */
  selectedProviders: ProviderSummary[];

  // ── Build step 1: Preguntas ─────────────────────────────────────────────

  /** AI-generated personalisation questions for the presentation. */
  questions: WizardQuestion[];
  /**
   * Record of question ID → user's answer string.
   * For multi_select questions, selected options are joined with " | ".
   * The optional free-text addendum is appended after " — ".
   */
  questionAnswers: Record<string, string>;
  /** Zero-based index of the question currently displayed (one-at-a-time flow). */
  currentQuestionIndex: number;
  /** True while the AI is generating the questions list. */
  isQuestionsLoading: boolean;

  // ── Build step 2: Planificación ─────────────────────────────────────────

  /** Structured narrative plan returned by generate-plan. */
  plan: NarrativePlan | null;
  /** Editable markdown representation of the plan (used in the chat UI). */
  planMarkdown: string;
  /** True while the AI is generating the initial plan (set by the parent shell). */
  isPlanGenerating: boolean;
  /** Last error message from the plan-chat assistant, if any. */
  planAssistError: string | null;

  // ── Build step 3: Diapositivas ──────────────────────────────────────────

  /** Ordered slide configuration array produced by generate-slides. */
  slides: SlideConfig[];
  /** Index into `slides` of the currently focused slide, or `null`. */
  selectedSlideIndex: number | null;

  // ── Build step 4: Imágenes ──────────────────────────────────────────────

  /**
   * Map of `"${slideIndex}-${imageIndex}"` → slot runtime state.
   * Tracks generation progress per image independently.
   */
  imageSlots: Record<string, ImageSlotState>;
  /** Reference images pinned for contextual generation. */
  referenceImages: ReferenceImage[];
  /**
   * IDs of reference images currently selected for the next generation call.
   * Stored as a Set for O(1) membership checks.
   */
  selectedReferenceIds: Set<string>;

  // ── Build step 5: Costos ────────────────────────────────────────────────

  /** Cost tables collected from chat/quotation sessions. */
  costTables: CostTableConfig[];
  /** Index into `costTables` of the table currently open in the editor. */
  selectedCostTableIndex: number | null;
}

// ─── Actions interface ───────────────────────────────────────────────────────

interface WizardActions {
  // ── Navigation ──────────────────────────────────────────────────────────

  /**
   * Navigate to a specific Setup step.
   * Switches the active bar to "setup" if it is currently "build".
   */
  goToSetupStep: (step: SetupStep) => void;

  /**
   * Navigate to a specific Build step.
   * Only allowed when `activeBar === 'build'`.
   */
  goToBuildStep: (step: BuildStep) => void;

  /**
   * Begin the animated transition from Setup bar to Build bar.
   * Sets `isTransitioning = true`; caller must call `finishTransition` when animation completes.
   */
  transitionToBar2: () => void;

  /**
   * Complete the bar transition — sets `activeBar = 'build'` and clears `isTransitioning`.
   * Called by the animation orchestrator in the shell component.
   */
  finishTransition: () => void;

  /**
   * Return to the Setup bar from the Build bar.
   * Emits a warning that build steps may need to be re-run.
   */
  returnToSetup: () => void;

  // ── Setup data ──────────────────────────────────────────────────────────

  /** Merge a partial update into `formData`. */
  updateFormData: (partial: Partial<WizardFormData>) => void;

  /** Store the presentation UUID after the draft is created on the backend. */
  setPresentationId: (id: string) => void;

  /**
   * Replace the full selected-providers list and sync `provider_ids`
   * in `formData` automatically.
   */
  setSelectedProviders: (providers: ProviderSummary[]) => void;

  // ── Questions ────────────────────────────────────────────────────────────

  /** Replace the questions list and reset navigation to the first question. */
  setQuestions: (questions: WizardQuestion[]) => void;

  /**
   * Set or update the answer for a specific question.
   *
   * @param id - Question ID (matches `WizardQuestion.id`).
   * @param answer - User-provided answer string.
   */
  setQuestionAnswer: (id: string, answer: string) => void;

  /** Navigate to a specific question by zero-based index. */
  setCurrentQuestionIndex: (index: number) => void;

  /** Toggle the questions-loading flag. */
  setIsQuestionsLoading: (v: boolean) => void;

  // ── Plan ─────────────────────────────────────────────────────────────────

  /** Replace the current narrative plan. Pass `null` to clear. */
  setPlan: (plan: NarrativePlan | null) => void;

  /** Update the editable markdown representation. */
  setPlanMarkdown: (md: string) => void;

  /** Toggle the plan-generating flag (set by the parent shell). */
  setIsPlanGenerating: (v: boolean) => void;

  /** Set or clear the plan assistant error message. */
  setPlanAssistError: (error: string | null) => void;

  // ── Slides ───────────────────────────────────────────────────────────────

  /** Replace the entire slides array. */
  setSlides: (slides: SlideConfig[]) => void;

  /**
   * Move a slide from `fromIndex` to `toIndex` (drag-and-drop reorder).
   * Adjusts `selectedSlideIndex` if it pointed at the moved slide.
   */
  reorderSlides: (fromIndex: number, toIndex: number) => void;

  /** Merge a partial update into the slide at the given index. */
  updateSlide: (index: number, partial: Partial<SlideConfig>) => void;

  /**
   * Remove the slide at the given index.
   * Adjusts `selectedSlideIndex` if needed.
   */
  removeSlide: (index: number) => void;

  /** Set the currently focused slide index. */
  setSelectedSlideIndex: (index: number | null) => void;

  // ── Images ───────────────────────────────────────────────────────────────

  /**
   * Write the result of an image generation or background-removal call
   * into the slot identified by key `"${slideIndex}-${imageIndex}"`.
   */
  setImageSlotResult: (
    key: string,
    url: string | null,
    status: ImageSlotState['status'],
  ) => void;

  /** Initialise an image slot. If `existingUrl` is provided the slot starts as `done`. */
  initImageSlot: (
    key: string,
    slot: Omit<ImageSlotState, 'url' | 'status'>,
    existingUrl?: string,
  ) => void;

  /** Mark an image slot as currently generating. */
  setImageSlotGenerating: (key: string) => void;

  /** Append a reference image to the list (deduplicates by `id`). */
  addReferenceImage: (image: ReferenceImage) => void;

  /** Toggle the selected state of a reference image. */
  toggleReferenceImage: (id: string) => void;

  // ── Costs ────────────────────────────────────────────────────────────────

  /** Replace the entire cost-tables array. */
  setCostTables: (tables: CostTableConfig[]) => void;

  /** Replace a single cost table at the given index. */
  updateCostTable: (index: number, table: CostTableConfig) => void;

  /** Set the currently open cost-table index. */
  setSelectedCostTableIndex: (index: number | null) => void;

  // ── Reset ────────────────────────────────────────────────────────────────

  /** Reset all state to initial values (e.g. on "Create New"). */
  reset: () => void;
}

// ─── Initial state ───────────────────────────────────────────────────────────

const INITIAL_STATE: WizardState = {
  presentationId: null,

  activeBar: 'setup',
  setupStep: 1,
  buildStep: 1,
  highestSetupStep: 1,
  highestBuildStep: 1,
  isTransitioning: false,

  formData: INITIAL_FORM_DATA,
  selectedProviders: [],

  questions: [],
  questionAnswers: {},
  currentQuestionIndex: 0,
  isQuestionsLoading: false,

  plan: null,
  planMarkdown: '',
  isPlanGenerating: false,
  planAssistError: null,

  slides: [],
  selectedSlideIndex: null,

  imageSlots: {},
  referenceImages: [],
  selectedReferenceIds: new Set<string>(),

  costTables: [],
  selectedCostTableIndex: null,
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useWizardStore = create<WizardState & WizardActions>((set, get) => ({
  ...INITIAL_STATE,

  // ── Navigation ────────────────────────────────────────────────────────────

  goToSetupStep: (step) =>
    set((s) => ({
      activeBar: 'setup',
      setupStep: step,
      highestSetupStep: Math.max(s.highestSetupStep, step),
    })),

  goToBuildStep: (step) =>
    set((s) => ({
      buildStep: step,
      highestBuildStep: Math.max(s.highestBuildStep, step),
    })),

  transitionToBar2: () => set({ isTransitioning: true }),

  finishTransition: () => set({ activeBar: 'build', isTransitioning: false }),

  returnToSetup: () => set({ activeBar: 'setup', isTransitioning: false }),

  // ── Setup data ────────────────────────────────────────────────────────────

  updateFormData: (partial) =>
    set((s) => ({ formData: { ...s.formData, ...partial } })),

  setPresentationId: (id) => set({ presentationId: id }),

  setSelectedProviders: (providers) =>
    set((s) => ({
      selectedProviders: providers,
      formData: {
        ...s.formData,
        provider_ids: providers.map((p) => p.id),
      },
    })),

  // ── Questions ─────────────────────────────────────────────────────────────

  setQuestions: (questions) => set({ questions, currentQuestionIndex: 0 }),

  setQuestionAnswer: (id, answer) =>
    set((s) => ({
      questionAnswers: { ...s.questionAnswers, [id]: answer },
    })),

  setCurrentQuestionIndex: (index) => set({ currentQuestionIndex: index }),

  setIsQuestionsLoading: (v) => set({ isQuestionsLoading: v }),

  // ── Plan ──────────────────────────────────────────────────────────────────

  setPlan: (plan) => set({ plan }),

  setPlanMarkdown: (md) => set({ planMarkdown: md }),

  setIsPlanGenerating: (v) => set({ isPlanGenerating: v }),

  setPlanAssistError: (error) => set({ planAssistError: error }),

  // ── Slides ────────────────────────────────────────────────────────────────

  setSlides: (slides) => set({ slides }),

  reorderSlides: (fromIndex, toIndex) => {
    const { slides, selectedSlideIndex } = get();
    const next = [...slides];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    let nextSelected = selectedSlideIndex;
    if (selectedSlideIndex === fromIndex) {
      nextSelected = toIndex;
    } else if (
      selectedSlideIndex !== null &&
      fromIndex < selectedSlideIndex &&
      toIndex >= selectedSlideIndex
    ) {
      nextSelected = selectedSlideIndex - 1;
    } else if (
      selectedSlideIndex !== null &&
      fromIndex > selectedSlideIndex &&
      toIndex <= selectedSlideIndex
    ) {
      nextSelected = selectedSlideIndex + 1;
    }

    set({ slides: next, selectedSlideIndex: nextSelected });
  },

  updateSlide: (index, partial) =>
    set((s) => ({
      slides: s.slides.map((sl, i) => (i === index ? { ...sl, ...partial } : sl)),
    })),

  removeSlide: (index) => {
    const { slides, selectedSlideIndex } = get();
    if (slides.length <= 1) return;
    const next = slides.filter((_, i) => i !== index);
    const nextSelected =
      selectedSlideIndex === null
        ? null
        : selectedSlideIndex >= index
          ? Math.max(0, selectedSlideIndex - 1)
          : selectedSlideIndex;
    set({ slides: next, selectedSlideIndex: nextSelected });
  },

  setSelectedSlideIndex: (index) => set({ selectedSlideIndex: index }),

  // ── Images ────────────────────────────────────────────────────────────────

  setImageSlotResult: (key, url, status) =>
    set((s) => {
      const newImageSlots = {
        ...s.imageSlots,
        [key]: { ...s.imageSlots[key], url, status },
      };

      // Also write the URL into the slide's images array so it persists
      // when slides are saved to the backend.
      if (url && status === 'done') {
        const [siStr, iiStr] = key.split('-');
        const si = Number(siStr);
        const ii = Number(iiStr);
        if (!isNaN(si) && si < s.slides.length) {
          const slide = s.slides[si];
          const images = [...(slide.images ?? [])];
          while (images.length <= ii) {
            images.push({ id: `s${si}-${images.length}`, url: '', prompt: '', style: 'content', aspect_ratio: '16:9' });
          }
          images[ii] = { ...images[ii], url };
          const newSlides = [...s.slides];
          newSlides[si] = { ...slide, images, image_url: images[0]?.url ?? slide.image_url };
          return { imageSlots: newImageSlots, slides: newSlides };
        }
      }

      return { imageSlots: newImageSlots };
    }),

  initImageSlot: (key, slot, existingUrl) =>
    set((s) => {
      const existing = s.imageSlots[key];
      if (existing?.url && existing.status === 'done') return {};
      return {
        imageSlots: {
          ...s.imageSlots,
          [key]: {
            ...slot,
            url: existingUrl ?? null,
            status: existingUrl ? 'done' as const : 'idle' as const,
          },
        },
      };
    }),

  setImageSlotGenerating: (key) =>
    set((s) => ({
      imageSlots: {
        ...s.imageSlots,
        [key]: { ...s.imageSlots[key], status: 'generating' },
      },
    })),

  addReferenceImage: (image) =>
    set((s) => {
      const idx = s.referenceImages.findIndex((r) => r.id === image.id);
      if (idx >= 0) {
        if (s.referenceImages[idx].url === image.url) return {};
        const updated = [...s.referenceImages];
        updated[idx] = { ...updated[idx], ...image };
        return { referenceImages: updated };
      }
      return { referenceImages: [...s.referenceImages, image] };
    }),

  toggleReferenceImage: (id) =>
    set((s) => {
      const next = new Set(s.selectedReferenceIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { selectedReferenceIds: next };
    }),

  // ── Costs ─────────────────────────────────────────────────────────────────

  setCostTables: (tables) => set({ costTables: tables }),

  updateCostTable: (index, table) =>
    set((s) => ({
      costTables: s.costTables.map((t, i) => (i === index ? table : t)),
    })),

  setSelectedCostTableIndex: (index) => set({ selectedCostTableIndex: index }),

  // ── Reset ─────────────────────────────────────────────────────────────────

  reset: () =>
    set({
      ...INITIAL_STATE,
      // Re-create the Set so the reference is fresh.
      selectedReferenceIds: new Set<string>(),
    }),
}));
